import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
export function Hero() {
  const customEase = [0.16, 1, 0.3, 1];
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const wordVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: customEase,
        delay: i * 0.15
      }
    })
  };
  return <section id="home" ref={containerRef} className="relative min-h-screen bg-parchment pt-32 pb-20 lg:pt-0 lg:pb-0 flex items-center overflow-hidden texture-grain" data-id="element-226">
      {/* Signature Arc Motif */}
      <svg className="absolute left-[-10%] top-[20%] w-[60%] h-auto text-copper opacity-10 pointer-events-none" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-227">
        <path d="M50 450C150 200 350 100 450 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-228" />
      </svg>

      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 w-full" data-id="element-229">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-16 lg:gap-8" data-id="element-230">
          {/* Left Content (55%) */}
          <div className="w-full lg:w-[55%] relative z-10 pt-10 lg:pt-20" data-id="element-231">
            <h1 className="text-6xl md:text-8xl lg:text-[8rem] leading-[0.9] tracking-tight mb-8" data-id="element-232">
              <div className="overflow-hidden" data-id="element-233">
                <motion.span custom={0} variants={wordVariants} initial="hidden" animate="visible" className="block font-serif font-light text-ink" data-id="element-234">
                  Master the Art of
                </motion.span>
              </div>
              <div className="overflow-hidden flex items-center gap-4 md:gap-8" data-id="element-235">
                <motion.span custom={1} variants={wordVariants} initial="hidden" animate="visible" className="block font-serif font-semibold italic text-copper" data-id="element-236">
                  Makeup
                </motion.span>
                <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 1.2,
                delay: 0.8,
                ease: customEase
              }} className="h-[2px] bg-copper flex-grow origin-left mt-4 md:mt-8 hidden sm:block" data-id="element-237" />
              </div>
              <div className="overflow-hidden mt-2" data-id="element-238">
                <motion.span custom={2} variants={wordVariants} initial="hidden" animate="visible" className="block font-serif font-light text-ink text-5xl md:text-7xl lg:text-[5.5rem]" data-id="element-239">
                  In Person.
                </motion.span>
              </div>
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.8,
            ease: customEase
          }} className="max-w-xl" data-id="element-240">
              <p className="text-lg md:text-xl text-stone font-sans mb-10 leading-relaxed" data-id="element-241">
                Premium in-person masterclasses in Brisbane, Sydney, and
                Melbourne. From $495. Beginner-friendly. Confidence-building.
                Unforgettable.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-242">
                <a href="#masterclasses" className="group relative inline-flex items-center justify-center px-8 py-4 bg-ink text-parchment overflow-hidden transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-243">
                  <span className="relative z-10 text-xs uppercase tracking-[0.2em] font-semibold" data-id="element-244">
                    View Masterclasses
                  </span>
                  <div className="absolute inset-0 bg-espresso translate-y-[100%] group-hover:translate-y-0 transition-transform duration-500 ease-[0.16,1,0.3,1]" data-id="element-245" />
                </a>

                <div className="flex flex-col gap-2" data-id="element-246">
                  <a href="#private" className="text-sm font-sans text-stone hover:text-copper transition-colors border-b border-transparent hover:border-copper pb-0.5" data-id="element-247">
                    Also offering bridal & private events &rarr;
                  </a>
                  <a href="#contact-form" className="text-xs font-sans text-copper hover:text-ink transition-colors border-b border-transparent hover:border-ink pb-0.5" data-id="element-248">
                    Give the gift of confidence &rarr;
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Image Placeholder (45%) */}
          <div className="w-full lg:w-[45%] h-[60vh] lg:h-screen relative lg:-mr-12 overflow-hidden" data-id="element-249">
            <motion.div style={{
            y
          }} initial={{
            scale: 1.05,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 1.5,
            ease: customEase
          }} className="absolute inset-0 bg-cream-dark flex items-center justify-center overflow-hidden" data-id="element-250">
              <img src="https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=900&q=80" alt="Makeup brushes and beauty tools" className="w-full h-full object-cover img-editorial" data-id="element-251" />
              <div className="warm-overlay" data-id="element-252"></div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}